import { getFragmentData } from '@e-socna/data-layer';

import { useMissionContextOptional } from '../../../../contexts/MissionContext/hooks/useMissionContextOptional.hook';
import { MissionContextStatus } from '../../../../contexts/MissionContext/types/mission-context.type';
import { MissionQuotationPage_QuotationInfo_Fragment } from '../../../../pages/MissionQuotation/fragments/quotation-info.fragment';

import type {
  ResolverParameters,
  ResolverResult,
} from '../../types/resolvers.type';
import type { QuotationHeaderConditionsInput } from '../utils/quotation-header-conditions.util';

// A boolean resolver telling something of the header of the quotation, used as
// a display condition of its sections.
export const useQuotationHeaderCondition = (
  { enabled }: Readonly<ResolverParameters>,
  condition: (header: Readonly<QuotationHeaderConditionsInput>) => boolean,
): ResolverResult<boolean> => {
  const missionContext = useMissionContextOptional();

  // A template has no mission: its conditions stay unresolved, and its
  // sections displayed.
  if (!enabled || !missionContext) {
    return {
      data: undefined,
      error: null,
      isSuccess: false,
      isLoading: false,
    };
  }

  const { status, mission } = missionContext;

  switch (status) {
    case MissionContextStatus.Error:
      return {
        data: undefined,
        error: new Error('Error loading mission context'),
        isSuccess: false,
        isLoading: false,
      };
    case MissionContextStatus.Loading:
      return {
        data: undefined,
        error: null,
        isSuccess: false,
        isLoading: true,
      };
    case MissionContextStatus.NotInitialized:
      return {
        data: undefined,
        error: new Error('Mission context not initialized'),
        isSuccess: false,
        isLoading: false,
      };
    case MissionContextStatus.Ready: {
      // Conditions are resolved as soon as the document is rendered, even
      // before its mission has quotation information: then it has nothing.
      const quotationInfo = getFragmentData(
        MissionQuotationPage_QuotationInfo_Fragment,
        mission.quotationInfo,
      );

      return {
        data: {
          generated: quotationInfo ? condition(quotationInfo.header) : false,
        },
        error: null,
        isSuccess: true,
        isLoading: false,
      };
    }
    default: {
      throw new Error('Unhandled mission context status');
    }
  }
};
